import { useQuery } from '@tanstack/react-query';
import Badge, { StatusBadge } from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import Spinner from '../ui/Spinner';
import { getCandidateSla } from '../../api/candidates';

/** 93784 → "1d 2h 3m"; under a minute shows seconds. */
export function formatDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return [d && `${d}d`, (d || h) && `${h}h`, `${m}m`].filter(Boolean).join(' ');
}

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="text-right text-gray-800">{children}</span>
    </div>
  );
}

export default function SlaTab({ candidate }) {
  const query = useQuery({ queryKey: ['candidate-sla', candidate.id], queryFn: () => getCandidateSla(candidate.id) });

  if (query.isLoading) return <Spinner className="mx-auto my-6" />;
  const steps = query.data || [];
  if (steps.length === 0) return <EmptyState title="No steps reached yet." />;

  return (
    <ol className="space-y-3">
      {steps.map((s) => {
        const overSla = s.sla_days && s.duration_seconds > s.sla_days * 86400;
        return (
          <li key={s.id} className="rounded-md border border-gray-200 p-3">
            <div className="mb-2 flex items-start justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">{s.title}</span>
              <StatusBadge status={s.status} />
            </div>

            <div className="space-y-1">
              <Row label="Reached">
                {when(s.reached_at)}
                {s.reached_estimated && <span className="text-gray-400"> (est.)</span>}
              </Row>

              {s.approvals.map((a) => (
                <div key={a.id} className="space-y-1 rounded bg-gray-50 px-2 py-1.5">
                  <Row label={`Approval requested${s.approvals.length > 1 || a.level ? ` · L${a.level}` : ''}`}>
                    {when(a.requested_at)}
                    {a.requested_by && <span className="text-gray-400"> · {a.requested_by}</span>}
                  </Row>
                  {a.status === 'pending' ? (
                    <Row label="Approval">
                      <Badge tone="amber">Pending</Badge>
                    </Row>
                  ) : (
                    <>
                      <Row label={a.status === 'approved' ? 'Approved' : 'Rejected'}>
                        {when(a.reviewed_at)}
                        {a.reviewed_by && <span className="text-gray-400"> · {a.reviewed_by}</span>}
                      </Row>
                      <Row label="Approval took">{formatDuration(a.wait_seconds)}</Row>
                    </>
                  )}
                </div>
              ))}

              {s.is_finished && (
                <Row label="Completed">
                  {when(s.completed_at)}
                  {s.completed_by && <span className="text-gray-400"> · {s.completed_by}</span>}
                </Row>
              )}
              <Row label={s.is_finished ? 'Time taken' : 'Time so far'}>
                <span className={`font-semibold ${overSla ? 'text-red-600' : 'text-gray-900'}`}>{formatDuration(s.duration_seconds)}</span>
                {s.sla_days ? <span className="text-gray-400"> / SLA {s.sla_days}d</span> : null}
              </Row>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Same data as the SLA tab, laid out as a table for the wide timeline popup. */
export function SlaTable({ candidateId }) {
  const query = useQuery({ queryKey: ['candidate-sla', candidateId], queryFn: () => getCandidateSla(candidateId) });

  if (query.isLoading) return <p className="py-8 text-center text-sm text-gray-500">Loading SLA…</p>;
  if (query.isError) return <p className="py-8 text-center text-sm text-red-600">Could not load SLA.</p>;
  const steps = query.data || [];
  if (steps.length === 0) return <p className="py-8 text-center text-sm text-gray-400">No steps reached yet.</p>;

  const person = (name) => (name ? <div className="text-xs text-gray-400">{name}</div> : null);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Step', 'Status', 'Time taken', 'Reached', 'Approval requested', 'Approved / rejected', 'Approval took', 'Completed'].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {steps.map((s) => {
            const overSla = s.sla_days && s.duration_seconds > s.sla_days * 86400;
            return (
              <tr key={s.id} className="align-top">
                <td className="px-3 py-2 font-medium text-gray-900">{s.title}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={s.status} />
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <span className={`font-semibold ${overSla ? 'text-red-600' : 'text-gray-900'}`}>{formatDuration(s.duration_seconds)}</span>
                  {!s.is_finished && <span className="text-xs text-gray-400"> so far</span>}
                  {s.sla_days ? <div className="text-xs text-gray-400">SLA {s.sla_days}d</div> : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                  {when(s.reached_at)}
                  {s.reached_estimated && <span className="text-gray-400"> (est.)</span>}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {s.approvals.length === 0 ? '—' : s.approvals.map((a) => (
                    <div key={a.id} className="mb-1 last:mb-0">
                      <div className="whitespace-nowrap">{when(a.requested_at)}</div>
                      {person(a.requested_by)}
                    </div>
                  ))}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {s.approvals.length === 0 ? '—' : s.approvals.map((a) => (
                    <div key={a.id} className="mb-1 last:mb-0">
                      {a.status === 'pending' ? (
                        <Badge tone="amber">Pending</Badge>
                      ) : (
                        <>
                          <div className="whitespace-nowrap">
                            {when(a.reviewed_at)} <span className={a.status === 'approved' ? 'text-green-700' : 'text-red-600'}>({a.status})</span>
                          </div>
                          {person(a.reviewed_by)}
                        </>
                      )}
                    </div>
                  ))}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {s.approvals.length === 0 ? '—' : s.approvals.map((a) => <div key={a.id} className="mb-1 last:mb-0">{formatDuration(a.wait_seconds)}</div>)}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {s.is_finished ? (
                    <>
                      <div className="whitespace-nowrap">{when(s.completed_at)}</div>
                      {person(s.completed_by)}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
